import * as THREE from 'three';
import { Scene, Renderer, Controls, Lighting } from './core/index.js';
import { HumanModel, AnimationManager } from './human/index.js';
import { MaterialOptimizer } from './human/index.js';
import { ResizeHandler, LoadingScreen } from './utils/index.js';
import { ControlPanel } from './ui/index.js';
import { ModelManager } from './manager/ModelManager.js';
import './style.css';

/**
 * 数字人主应用入口
 * 聚合所有模块，控制生命周期和渲染循环
 */
class App {
  #scene;
  #renderer;
  #controls;
  #lighting;
  #resizeHandler;
  #loadingScreen;
  #controlPanel;
  #modelManager;

  /** 当前显示的数字人实例 */
  #currentHumanModel = null;
  /** 当前模型场景对象 */
  #currentModelScene = null;

  /** 上一帧时间戳（用于 delta 计算） */
  #lastTime = 0;
  /** 渲染循环 ID */
  #animationId = null;
  /** 自动旋转速度 */
  #rotationSpeed = 0;
  /** 帧计数器（用于 UI 更新节流） */
  #frameCount = 0;

  constructor() {
    console.log('[App] 初始化数字人应用...');

    // 加载进度 UI
    this.#loadingScreen = new LoadingScreen();
    this.#loadingScreen.show();

    // 1. 初始化场景
    this.#scene = new Scene();

    // 2. 初始化渲染器
    const canvas = document.querySelector('#webgl-canvas');
    this.#renderer = new Renderer(canvas);

    // 3. 初始化控制器
    this.#controls = new Controls(this.#scene.camera, this.#renderer.renderer);

    // 4. 初始化光照
    this.#lighting = new Lighting(this.#scene.scene);

    // 5. 初始化控制面板
    this.#controlPanel = new ControlPanel({
      // 动画控制
      onPlayAnimation: (name) => {
        this.#currentHumanModel?.playAnimation(name);
      },
      onPauseAnimation: () => {
        this.#currentHumanModel?.pauseAnimation();
      },
      onStopAnimation: () => {
        this.#currentHumanModel?.stopAnimation();
      },
      onSetAnimationSpeed: (speed) => {
        this.#currentHumanModel?.setAnimationSpeed(speed);
      },
      // 光照调节
      onSetAmbientIntensity: (v) => {
        this.#lighting.setIntensity('ambient', v);
      },
      onSetMainLightIntensity: (v) => {
        this.#lighting.setIntensity('main', v);
      },
      onSetExposure: (v) => {
        this.#renderer.renderer.toneMappingExposure = v;
      },
      // 模型调节
      onSetModelScale: (v) => {
        if (this.#currentModelScene) {
          this.#currentModelScene.scale.setScalar(v);
        }
      },
      onSetRotationSpeed: (v) => {
        this.#rotationSpeed = v;
      },
      onSetRoughness: (v) => {
        if (this.#currentModelScene) {
          MaterialOptimizer.setGlobalMaterialParams(this.#currentModelScene, { roughness: v });
        }
      },
      onSetMetalness: (v) => {
        if (this.#currentModelScene) {
          MaterialOptimizer.setGlobalMaterialParams(this.#currentModelScene, { metalness: v });
        }
      },
      // 操作
      onResetCamera: () => {
        this.#controls.resetCamera();
      },
      onScreenshot: () => {
        this.#takeScreenshot();
      },
      onPreset: (name) => {
        this.#applyMaterialPreset(name);
      },
    });

    // 6. 初始化模型管理器
    this.#modelManager = new ModelManager({
      // 模型被选中时显示
      onSelectModel: (id, modelData) => {
        this.#showModel(id, modelData);
      },
      // 模型被移除时隐藏
      onRemoveModel: (id, modelData) => {
        if (id === this.#modelManager.getActiveId()) {
          this.#hideCurrentModel();
        }
        // 非活跃模型不在场景中，无需移除
      },
      // 新模型加载完成
      onModelLoaded: (id, modelData) => {
        console.log(`[App] 模型已加载: ${modelData.name} (${modelData.animationCount} 个动画)`);
      },
    });

    // 7. 初始化窗口自适应
    this.#resizeHandler = new ResizeHandler((width, height) => {
      this.#onResize(width, height);
    });
    this.#resizeHandler.start();

    // 7.5 移动端面板切换
    this.#setupMobileToggle();

    // 8. 启动渲染循环
    this.#lastTime = performance.now();
    this.#animationId = requestAnimationFrame(this.#animate.bind(this));

    // 9. 预加载示例模型列表
    this.#loadSampleModels();

    console.log('[App] 初始化完成');
  }

  /** 窗口大小变化处理 */
  #onResize(width, height) {
    this.#renderer.setSize(width, height);
    this.#scene.onResize(width, height);
  }

  /** 移动端面板切换逻辑 */
  #setupMobileToggle() {
    const overlay = document.getElementById('mobile-overlay');
    const tabBar = document.getElementById('mobile-tab-bar');
    const modelsTab = document.getElementById('mobile-tab-models');
    const panelTab = document.getElementById('mobile-tab-panel');
    const modelPanel = document.getElementById('model-manager');
    const ctrlPanel = document.getElementById('control-panel');

    const hideAll = () => {
      modelPanel?.classList.add('collapsed');
      ctrlPanel?.classList.add('collapsed');
      overlay?.classList.remove('active');
      modelsTab?.classList.remove('active');
      panelTab?.classList.remove('active');
    };

    const togglePanel = (panel, tabEl, panelId) => {
      const isActive = !panel?.classList.contains('collapsed');
      hideAll();
      if (!isActive) {
        panel?.classList.remove('collapsed');
        overlay?.classList.add('active');
        tabEl?.classList.add('active');
      }
    };

    modelsTab?.addEventListener('click', () => togglePanel(modelPanel, modelsTab, 'model'));
    panelTab?.addEventListener('click', () => togglePanel(ctrlPanel, panelTab, 'panel'));
    overlay?.addEventListener('click', hideAll);

    // 移动端默认折叠两侧面板
    if (window.innerWidth <= 768) {
      modelPanel?.classList.add('collapsed');
      ctrlPanel?.classList.add('collapsed');
    }
  }

  /** 示例模型列表配置 */
  #SAMPLE_MODELS = [
    { id: 'digital-human', file: 'digital-human.glb', name: '数字人', icon: '🧍' },
    { id: 'soldier', file: 'Soldier.glb', name: '士兵', icon: '🎖' },
    { id: 'horse', file: 'Horse.glb', name: '马', icon: '🐎' },
    { id: 'flamingo', file: 'Flamingo.glb', name: '火烈鸟', icon: '🦩' },
    { id: 'parrot', file: 'Parrot.glb', name: '鹦鹉', icon: '🦜' },
    { id: 'tokyo', file: 'LittlestTokyo.glb', name: '小东京', icon: '🗼' },
  ];

  /** 预加载所有示例模型 */
  async #loadSampleModels() {
    let loadedCount = 0;

    for (const sample of this.#SAMPLE_MODELS) {
      try {
        const response = await fetch(`/models/${sample.file}`);
        if (!response.ok) continue;

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const result = await this.#modelManager.loader.load(url);
        URL.revokeObjectURL(url);

        // 生成缩略图（失败则用名称图标兜底）
        const thumbnail = this.#modelManager.generateThumbnail(result.scene) || this.#modelManager.generateIcon(sample.name);

        // 注册到管理器
        this.#modelManager.registerModel(sample.id, {
          scene: result.scene,
          animations: result.animations,
          name: sample.name,
          fileName: sample.file,
          animationCount: result.animations.length,
          thumbnail: thumbnail || '',
        });

        loadedCount++;
        console.log(`[App] 示例模型已加载: ${sample.name} (${result.animations.length} 个动画)`);
      } catch (err) {
        console.warn(`[App] 示例模型加载失败: ${sample.name}`, err.message);
      }
    }

    console.log(`[App] 共加载 ${loadedCount}/${this.#SAMPLE_MODELS.length} 个示例模型`);

    // 默认选中第一个
    if (loadedCount > 0) {
      this.#modelManager.selectModel(this.#SAMPLE_MODELS[0].id);
    } else {
      this.#loadingScreen.hide();
    }
  }

  /** 显示指定模型（添加到场景） */
  #showModel(id, modelData) {
    // 先隐藏当前模型
    this.#hideCurrentModel();

    const humanModel = new HumanModel();
    const scene = modelData.scene;

    // 居中、缩放、材质优化
    humanModel.model = scene;
    this.#centerModelScene(scene);
    this.#autoScaleScene(scene);
    MaterialOptimizer.optimize(scene, {
      skinRoughness: 0.65,
      skinMetalness: 0.0,
      hairRoughness: 0.4,
      hairMetalness: 0.1,
      clothRoughness: 0.8,
      clothMetalness: 0.0,
      envMapIntensity: 1.0,
    });

    // 初始化动画
    const animMgr = new AnimationManager(scene);
    animMgr.initAnimationsFromClips(modelData.animations);
    humanModel.animationManager = animMgr;

    this.#currentHumanModel = humanModel;
    this.#currentModelScene = scene;

    // 添加到场景
    this.#scene.scene.add(scene);

    // 相机对准胸部位置
    const chestPos = this.#getChestPosition(scene);
    this.#controls.setTarget(chestPos.x, chestPos.y, chestPos.z);

    // 隐藏 loading
    this.#loadingScreen.hide();

    // 填充动画卡片（无动画时显示空提示）
    const details = humanModel.getAnimationDetails();
    console.log(`[App] ${modelData.name} 的动画列表:`, details.map(d => d.name).join(', ') || '（无动画）');
    if (details.length > 0) {
      this.#controlPanel.populateAnimations(details);
      humanModel.playAnimation(details[0].name);
    } else {
      this.#controlPanel.populateAnimations([]);
    }

    console.log(`[App] 已显示模型: ${modelData.name}`);
  }

  /** 隐藏当前模型（从场景移除，不销毁） */
  #hideCurrentModel() {
    if (this.#currentModelScene) {
      this.#scene.scene.remove(this.#currentModelScene);
      this.#currentHumanModel = null;
      this.#currentModelScene = null;
    }
  }

  /** 居中模型 X/Z，脚贴地 */
  #centerModelScene(model) {
    const box = new THREE.Box3().setFromObject(model);
    const center = box.getCenter(new THREE.Vector3());
    model.position.x -= center.x;
    model.position.y -= box.min.y;
    model.position.z -= center.z;
  }

  /** 自动缩放模型到合适大小 */
  #autoScaleScene(model) {
    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    if (maxDim > 0) {
      const scale = 2 / maxDim;
      model.scale.multiplyScalar(scale);
    }
  }

  /** 获取胸部位置（用于相机聚焦） */
  #getChestPosition(model) {
    const box = new THREE.Box3().setFromObject(model);
    const height = box.max.y - box.min.y;
    return new THREE.Vector3(0, height * 0.45, 0);
  }

  /** 截图保存为 PNG */
  #takeScreenshot() {
    this.#renderer.render(this.#scene.scene, this.#scene.camera);
    const dataURL = this.#renderer.renderer.domElement.toDataURL('image/png');
    const link = document.createElement('a');
    link.download = `digital-human-${Date.now()}.png`;
    link.href = dataURL;
    link.click();
  }

  /** 应用材质预设 */
  #applyMaterialPreset(name) {
    if (!this.#currentModelScene) return;

    const presets = {
      default: { roughness: undefined, metalness: undefined },
      smooth: { roughness: 0.2, metalness: 0.1 },
      metallic: { roughness: 0.3, metalness: 0.8 },
      matte: { roughness: 0.95, metalness: 0.0 },
    };

    const preset = presets[name];
    if (!preset) return;

    const roughnessSlider = document.getElementById('roughness-slider');
    const metalnessSlider = document.getElementById('metalness-slider');
    if (roughnessSlider) roughnessSlider.value = preset.roughness ?? 0.65;
    if (metalnessSlider) metalnessSlider.value = preset.metalness ?? 0.0;

    const params = {};
    if (preset.roughness !== undefined) params.roughness = preset.roughness;
    if (preset.metalness !== undefined) params.metalness = preset.metalness;
    MaterialOptimizer.setGlobalMaterialParams(this.#currentModelScene, params);
  }

  /** 渲染循环 */
  #animate(time) {
    this.#animationId = requestAnimationFrame(this.#animate.bind(this));

    // 计算帧时间差（秒）
    const delta = (time - this.#lastTime) / 1000;
    this.#lastTime = time;
    this.#frameCount++;

    // 更新控制器（阻尼效果）
    this.#controls.update();

    // 更新背景粒子
    this.#scene.updateParticles(delta);

    // 自动旋转模型
    if (this.#rotationSpeed > 0 && this.#currentModelScene) {
      this.#currentModelScene.rotation.y += this.#rotationSpeed * delta;
    }

    // 更新骨骼动画
    if (this.#currentHumanModel) {
      this.#currentHumanModel.updateAnimations(delta);
    }

    // 渲染场景
    this.#renderer.render(this.#scene.scene, this.#scene.camera);

    // 更新面板性能信息（每 30 帧约 0.5s 更新一次）
    if (this.#frameCount % 30 === 0) {
      const fps = Math.round(1 / (delta + 0.001));
      this.#controlPanel.updateFPS(fps);
      this.#controlPanel.updateTriangleCount(this.#renderer.renderer.info.render.triangles);
      if (this.#currentHumanModel) {
        const currentAnim = this.#currentHumanModel.getCurrentAnimation();
        if (currentAnim) {
          this.#controlPanel.setActiveAnimation(currentAnim);
        }
      }
    }
  }

  /** 销毁应用，释放资源 */
  dispose() {
    if (this.#animationId) {
      cancelAnimationFrame(this.#animationId);
    }
    this.#resizeHandler.stop();
    this.#controls.dispose();
    this.#renderer.dispose();
    this.#loadingScreen.destroy();
    this.#controlPanel.dispose();
    this.#modelManager.dispose();
    this.#hideCurrentModel();
  }
}

// 启动应用
const app = new App();

// 开发模式下暴露到 window，方便调试
if (import.meta.env.DEV) {
  window.app = app;
  window.THREE = THREE;
}
